import HomeIcon from "@mui/icons-material/Home";
import FlightIcon from "@mui/icons-material/Flight";
import moment from "moment";
import EmptyData from "@/components/EmptyData";
import "./index.scss";

export default function SeasonTimeline({
	matches,
	rosterId,
	navigate,
	compact = false,
	view = "all",
}) {
	if (!matches || !matches.length)
		return view === "form" ? null : <EmptyData />;

	const now = moment();
	const enriched = matches.map((m) => {
		const isHome = m.homeRosterId === rosterId;
		const goalsFor = isHome ? m.homeClubGoals : m.awayClubGoals;
		const goalsAgainst = isHome ? m.awayClubGoals : m.homeClubGoals;
		const opponent = isHome
			? m.awayTeamName || m.awayClub
			: m.homeTeamName || m.homeClub;
		const hasScore =
			Number.isInteger(goalsFor) && Number.isInteger(goalsAgainst);
		const isFuture = !hasScore && moment(m.startDate).isAfter(now);
		let result = null;
		let pts = 0;
		if (hasScore) {
			if (goalsFor > goalsAgainst) {
				result = "V";
				pts = 3;
			} else if (goalsFor === goalsAgainst) {
				result = "N";
				pts = 1;
			} else {
				result = "S";
				pts = 0;
			}
		}
		return {
			...m,
			isHome,
			goalsFor,
			goalsAgainst,
			opponent,
			hasScore,
			result,
			pts,
			isFuture,
		};
	});

	const scoredMatches = enriched.filter((m) => m.hasScore);
	const futureMatches = enriched.filter((m) => m.isFuture);
	const nextMatch = futureMatches.reduce(
		(next, match) =>
			!next || moment(match.startDate).isBefore(next.startDate) ? match : next,
		null,
	);
	const played = scoredMatches.length;
	const wins = scoredMatches.filter((m) => m.result === "V").length;
	const draws = scoredMatches.filter((m) => m.result === "N").length;
	const losses = scoredMatches.filter((m) => m.result === "S").length;
	const gf = scoredMatches.reduce((acc, m) => acc + (m.goalsFor || 0), 0);
	const gs = scoredMatches.reduce((acc, m) => acc + (m.goalsAgainst || 0), 0);

	const lastFive = scoredMatches.slice(-5);

	let cumPts = 0;
	const trendPoints = scoredMatches.map((m, i) => {
		cumPts += m.pts;
		return {x: i, y: cumPts};
	});

	const svgW = 300;
	const svgH = 48;
	const maxPts = scoredMatches.length * 3 || 1;
	const toX = (i) =>
		scoredMatches.length <= 1
			? svgW / 2
			: (i / (scoredMatches.length - 1)) * svgW;
	const toY = (p) => svgH - (p / maxPts) * (svgH - 4) - 2;
	const polylinePoints = trendPoints
		.map((p) => `${toX(p.x)},${toY(p.y)}`)
		.join(" ");

	return (
		<div
			className={`season-timeline${compact ? " season-timeline--compact" : ""} season-timeline--${view}`}
		>
			{view !== "form" && (
				<div className="season-timeline__stats">
					{[
						{label: "Giocate", value: played},
						{label: "V", value: wins, result: "v"},
						{label: "N", value: draws, result: "n"},
						{label: "S", value: losses, result: "s"},
						{label: "GF", value: gf},
						{label: "GS", value: gs},
						...(futureMatches.length > 0
							? [
									{
										label: "Programmate",
										value: futureMatches.length,
										upcoming: true,
									},
								]
							: []),
					].map(({label, value, highlight, upcoming, result}) => (
						<div
							key={label}
							className={
								"season-timeline__stat" +
								(highlight ? " season-timeline__stat--highlight" : "") +
								(result ? " season-timeline__stat--" + result : "") +
								(upcoming ? " season-timeline__stat--upcoming" : "")
							}
						>
							<span className="season-timeline__stat-label">{label}</span>
							<span className="season-timeline__stat-value">{value}</span>
						</div>
					))}
				</div>
			)}

			{view !== "timeline" && lastFive.length > 0 && (
				<div className="season-timeline__form">
					<span className="season-timeline__form-label">Forma</span>
					<div className="season-timeline__form-dots">
						{lastFive.map((m, i) => (
							<span
								key={m.id || i}
								className={
									"season-form-dot season-form-dot--" +
									(m.result === "V" ? "v" : m.result === "N" ? "n" : "s")
								}
								title={
									m.result === "V"
										? "Vittoria"
										: m.result === "N"
											? "Pareggio"
											: "Sconfitta"
								}
							>
								{m.result}
							</span>
						))}
					</div>
				</div>
			)}

			{view !== "timeline" && trendPoints.length >= 2 && (
				<div className="season-timeline__trend">
					<svg
						viewBox={`0 0 ${svgW} ${svgH}`}
						className="season-timeline__svg"
						preserveAspectRatio="none"
					>
						<polyline
							points={polylinePoints}
							fill="none"
							stroke="var(--color-primary)"
							strokeWidth="2.5"
							strokeLinejoin="round"
							strokeLinecap="round"
						/>
					</svg>
				</div>
			)}

			{view !== "form" && (
				<div className="season-timeline__scroll">
					{enriched.map((m, i) => (
						<div
							key={m.id || i}
							className={
								"season-match-chip season-match-chip--" +
								(m.isFuture
									? "upcoming"
									: m.result === "V"
										? "v"
										: m.result === "N"
											? "n"
											: m.result === "S"
												? "s"
												: "ns") +
								(m === nextMatch ? " season-match-chip--next" : "")
							}
							onClick={() => m.id && navigate("/matches/" + m.id)}
						>
							{m.competitionName && (
								<span
									className="season-match-chip__competition"
									title={m.competitionName}
								>
									{m.competitionName}
								</span>
							)}
							<span className="season-match-chip__date">
								{m.startDate ? moment(m.startDate).format("DD/MM") : "---"}
							</span>
							<span
								className={
									"season-match-chip__ha season-match-chip__ha--" +
									(m.isHome ? "h" : "a")
								}
								title={m.isHome ? "In casa" : "In trasferta"}
								aria-label={m.isHome ? "In casa" : "In trasferta"}
							>
								{m.isHome ? (
									<HomeIcon sx={{fontSize: 14}} />
								) : (
									<FlightIcon sx={{fontSize: 14}} />
								)}
							</span>
							<span className="season-match-chip__opponent" title={m.opponent}>
								{m.opponent || "---"}
							</span>
							{m.hasScore && (
								<span className="season-match-chip__score">
									{m.goalsFor}-{m.goalsAgainst}
								</span>
							)}
							{m.isFuture && m.startDate && (
								<span
									className="season-match-chip__time"
									title={
										m.startTimeToBeConfirmed ? "Orario da definire" : undefined
									}
								>
									{m.startTimeToBeConfirmed
										? "OdD"
										: moment(m.startDate).format("HH:mm")}
								</span>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
